import * as papaparse from 'papaparse';
import { download } from '../../Misc/helpers/download/download.helper';

/** A series of helpers for manually exporting data from an am chart model */
export const manualChartExport = {

    /**
     * Exports data for a single series of data
     *
     * @param type - format of exported data
     * @param data - raw data to export
     * @param dataFields - properties that should be included in exported data, and the labels for each
     * https://www.amcharts.com/docs/v4/reference/export/#dataFields_property
     * @param filename - name of the downloaded file
     */
    exportFlatSeriesData(type: string, data: any[], dataFields: object, filename): void {
        let formattedData;
        let blob;
        let downloadFileName;

        switch (type) {
            case 'json':
                formattedData = this.formatDataForDownload(data, dataFields);
                blob = new Blob([JSON.stringify(formattedData, null, 1)], { type: 'data:json;charset=utf-8;'});
                downloadFileName =  filename + '.json';

                break;
            case 'csv':
                formattedData = this.formatDataForDownload(data, dataFields);
                const csvString = papaparse.unparse({
                    fields: Object.values(dataFields),
                    data: formattedData
                });

                blob = new Blob(['\ufeff', csvString], { type: 'text/csv;charset=utf-8;'});
                downloadFileName =  filename + '.csv';

                break;
            default:
                return;
        }
        download.blob(blob, downloadFileName);
    },

    /**
     * Removes properties unwanted for exported data, and labels appropriately
     *
     * @param data - data to format
     * @param dataFields - object containing properties and labels to export
     *
     * @return object - data formatted with relevant properties and labels
     */
    formatDataForDownload(data: any[], dataFields: object): object {
        const formattedData = [];

        data.forEach(dataPoint => {
            const formattedDataPoint = {};
            const keys = Object.keys(dataPoint);

            keys.forEach(key => {
                const formattedKeyName = dataFields[key];
                if (!formattedKeyName) {
                    return;
                }
                formattedDataPoint[formattedKeyName] = dataPoint[key];
            });

            formattedData.push(formattedDataPoint);
        });

        return formattedData;
    },

    /**
     * Assembles data needed for export
     *
     * @param data - data to format
     * @param dataFields - object containing properties and labels to export
     * @param keyToCompare - key on each data point that will be compared across multiple series
     *
     * @return object - data formatted with relevant properties and labels
     */
    formatMultiSeriesDataForDownload(data: any[][], dataFields: object, keyToCompare: string): object {
        const formattedData = [];
        data[0].forEach( (dataPoint, index) => {
            const formattedDataPoint = {};
            const keys = Object.keys(dataPoint);

            keys.forEach(key => {
                let formattedKeyName = dataFields[key];
                if (key === keyToCompare) {
                    formattedKeyName = dataFields[key + 0];
                }

                if (!formattedKeyName) {
                    return;
                }
                formattedDataPoint[formattedKeyName] = dataPoint[key];
            });

            for (let i = 1; i < data.length; i++) {
                const seriesDataPoint = data[i][index];
                const valueToCompare = seriesDataPoint[keyToCompare];
                const formattedKey = dataFields[keyToCompare + i];
                formattedDataPoint[formattedKey] = valueToCompare;
            }

            formattedData.push(formattedDataPoint);
        });

        return formattedData;
    },

    /**
     * Exports data for a multiseries chart
     *
     * @param type - format of exported data
     * @param data - raw data to export
     * @param dataFields - properties that should be included in exported data, and the labels for each
     * https://www.amcharts.com/docs/v4/reference/export/#dataFields_property
     * @param filename - name of the downloaded file
     * @param keyToCompare - key on each data point that will be compared across multiple series
     */
    exportMultiSeriesData(type: string, data: any[][], dataFields: object, filename: string, keyToCompare: string): void {
        let formattedData;
        let blob;
        let downloadFileName;

        switch (type) {
            case 'json':
                formattedData = this.formatMultiSeriesDataForDownload(data, dataFields, keyToCompare);
                blob = new Blob([JSON.stringify(formattedData, null, 1)], { type: 'data:json;charset=utf-8;'});
                downloadFileName =  filename + '.json';

                break;
            case 'csv':
                formattedData = this.formatMultiSeriesDataForDownload(data, dataFields, keyToCompare);
                const csvString = papaparse.unparse({
                    fields: Object.values(dataFields),
                    data: formattedData
                });

                blob = new Blob(['\ufeff', csvString], { type: 'text/csv;charset=utf-8;'});
                downloadFileName =  filename + '.csv';

                break;
            default:
                return;
        }
        download.blob(blob, downloadFileName);
    }
};
